import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDropzone } from 'react-dropzone';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Boxes, Upload, FileText, FileCheck2, Sparkles, ShieldCheck, AlertTriangle,
  RefreshCw, Pencil, ChevronDown, ArrowLeft, CheckCircle2, Save, X,
  LayoutGrid, BedDouble, Bath, UtensilsCrossed, Trees, Sofa, DoorOpen, Briefcase,
  Plus, GitMerge, Camera, Images, Copy, Star,
} from 'lucide-react';
import HUDPageHeader from '../components/HUDPageHeader';
import { HudButton, HudBadge, HudCard } from '../components/hud';
import MaterialthequeHistoryDropdown from '../components/materialtheque/MaterialthequeHistoryDropdown';
import {
  EXTRACTION_PROGRESS_STEPS,
  ROOM_TYPES,
  getConfidenceTone,
  formatConfidence,
} from '../data/materialthequeMockData';
import MaterialThumbnail from '../components/materialtheque/MaterialThumbnail';
import MaterialEditDrawer from '../components/materialtheque/MaterialEditDrawer';
import CctpDiffPanel from '../components/materialtheque/CctpDiffPanel';
import SourceChooser from '../components/materialtheque/SourceChooser';
import PhotoSourcePanel from '../components/materialtheque/PhotoSourcePanel';
import AddMaterialDialog from '../components/materialtheque/AddMaterialDialog';
import CctpEnrichDialog from '../components/materialtheque/CctpEnrichDialog';
import { RoomRenderDialog, SectionRendersStrip } from '../components/materialtheque/RoomRender';
import DuplicatesPanel from '../components/materialtheque/DuplicatesPanel';
import MaterialBriefBanner from '../components/materialtheque/MaterialBriefBanner';
import { matApi, resolveThumbUrl } from '../api/materialthequeApi';

const ICON_MAP = {
  LayoutGrid, BedDouble, Bath, UtensilsCrossed, Trees, Sofa, DoorOpen, Briefcase, Boxes,
};

/**
 * MaterialThequePage — module Matériauthèque IA (extraction CCTP).
 *
 * 3 états :
 *  1. UPLOAD     — drag & drop du CCTP PDF
 *  2. EXTRACTING — skeleton + progress bar gold + messages dynamiques
 *  3. VALIDATE   — dashboard de validation par section + drawer d'édition
 */
const MaterialThequePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const presetProjectId = searchParams.get('project') || null;
  const compareMode = searchParams.get('compare') === '1';
  const presetJobId = searchParams.get('job') || null;

  const [phase, setPhase] = useState('upload');           // 'upload' | 'extracting' | 'validate'
  const [file, setFile] = useState(null);
  const [progressStep, setProgressStep] = useState(0);    // index dans EXTRACTION_PROGRESS_STEPS (mapping local pour skeleton)
  const [data, setData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [approving, setApproving] = useState(false);
  const [job, setJob] = useState(null);                   // job retourné par le backend
  const [jobError, setJobError] = useState(null);
  const [projectMeta, setProjectMeta] = useState(null);   // { project_id, name } si vient d'un projet
  const [loadingProject, setLoadingProject] = useState(false);
  const [saveStyleOpen, setSaveStyleOpen] = useState(false);
  const [styleName, setStyleName] = useState('');
  const [styleDescription, setStyleDescription] = useState('');
  const [savingStyle, setSavingStyle] = useState(false);
  const [source, setSource] = useState(null);             // null | 'cctp' | 'photos'
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [enrichOpen, setEnrichOpen] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [roomRenderSection, setRoomRenderSection] = useState(null); // section object
  const [renderingSectionId, setRenderingSectionId] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupDetecting, setDupDetecting] = useState(false);
  const [dupBusyGroup, setDupBusyGroup] = useState(null);
  const intervalRef = useRef(null);

  const isApproved = !!(job && job.approved_at);
  // V15.11 — arbitrage « Retenu »
  const [briefKey, setBriefKey] = useState(0);
  const briefProjectId = projectMeta?.project_id || data?.source?.project || job?.project_id || null;

  const reloadResult = useCallback(async (jobId) => {
    const result = await matApi.getJobResult(jobId);
    setData(result);
    return result;
  }, []);

  const handleRetain = useCallback(async (materialId) => {
    try {
      const r = await matApi.retainMaterial(materialId);
      toast.success(r.is_retained ? 'Matériau retenu pour son rôle' : 'Choix levé — tous les candidats seront envoyés');
      if (job?.job_id) await reloadResult(job.job_id);
      setBriefKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Arbitrage impossible');
    }
  }, [job, reloadResult]);

  const handleDetectDuplicates = async () => {
    if (!job?.job_id) return;
    setDupDetecting(true);
    try {
      const r = await matApi.detectDuplicates(job.job_id);
      await reloadResult(job.job_id);
      setDupOpen(true);
      if (r.count === 0) toast.success('Aucun doublon détecté');
      else toast.message(`${r.count} doublon${r.count > 1 ? 's' : ''} potentiel${r.count > 1 ? 's' : ''}`, { description: 'Vérifiez chaque groupe puis fusionnez.' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Détection impossible');
    } finally {
      setDupDetecting(false);
    }
  };

  const handleMergeGroup = async (group) => {
    if (!job?.job_id) return;
    const keepId = group.suggested_keep_id || group.ids[0];
    const mergeIds = group.ids.filter((id) => id !== keepId);
    setDupBusyGroup(group.group_id);
    try {
      const r = await matApi.mergeDuplicates(job.job_id, keepId, mergeIds);
      await reloadResult(job.job_id);
      toast.success(`Fusionné → ${r.rooms.join(', ')}`, {
        duration: 10000,
        action: {
          label: 'Annuler',
          onClick: async () => {
            try {
              await matApi.undoMerge(job.job_id, keepId);
              await reloadResult(job.job_id);
              toast.message('Fusion annulée');
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Impossible d'annuler");
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fusion impossible');
    } finally {
      setDupBusyGroup(null);
    }
  };

  const handleRoomRender = async (opts) => {
    const section = roomRenderSection;
    if (!job?.job_id || !section) return;
    setRenderingSectionId(section.id);
    setRoomRenderSection(null);
    toast.message(`Rendu « ${section.label} » en cours…`, { description: 'Environ 20 à 40 secondes', duration: 6000 });
    try {
      await matApi.renderRoom(job.job_id, section.id, opts);
      await reloadResult(job.job_id);
      toast.success(`Rendu de la pièce « ${section.label} » généré`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Erreur lors du rendu', { duration: 8000 });
    } finally {
      setRenderingSectionId(null);
    }
  };

  const handlePickSource = async (id) => {
    if (id === 'blank') {
      if (!presetProjectId) return;
      try {
        const created = await matApi.createBlankJob(presetProjectId);
        setJob(created);
        await reloadResult(created.job_id);
        setPhase('validate');
        toast.success('Matériauthèque vierge créée — ajoutez vos premiers matériaux');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Erreur de création');
      }
      return;
    }
    setSource(id);
  };

  const handlePhotosJobCreated = (created) => {
    setJob(created);
    setFile(null);
    setProgressStep(0);
    setJobError(null);
    setPhase('extracting');
  };

  const handleAddMaterial = async (form) => {
    if (!job?.job_id) return;
    setAdding(true);
    try {
      await matApi.addManualMaterial(job.job_id, form);
      await reloadResult(job.job_id);
      setAddOpen(false);
      toast.success(`« ${form.name} » ajouté`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setAdding(false);
    }
  };

  const handleEnrich = async (pdf) => {
    if (!job?.job_id || !pdf) return;
    setEnriching(true);
    try {
      const updated = await matApi.enrichFromCctp(job.job_id, pdf);
      setJob(updated);
      setFile(pdf);
      setEnrichOpen(false);
      setProgressStep(0);
      setPhase('extracting');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi du CCTP");
    } finally {
      setEnriching(false);
    }
  };

  const handleSaveAsStyle = async () => {
    if (!job?.job_id || !styleName.trim()) return;
    setSavingStyle(true);
    try {
      const r = await matApi.saveJobAsStyle(job.job_id, { name: styleName.trim(), description: styleDescription.trim() });
      toast.success(`Style "${r.style?.name}" sauvegardé`, {
        description: `${r.style?.materials_count} matériaux disponibles dans vos styles`,
      });
      setSaveStyleOpen(false);
      setStyleName('');
      setStyleDescription('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la sauvegarde du style');
    } finally {
      setSavingStyle(false);
    }
  };

  // ---------- UPLOAD ----------
  const onDrop = useCallback((accepted) => {
    if (!accepted?.length) return;
    const f = accepted[0];
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Format non supporté — uploadez un fichier PDF');
      return;
    }
    setFile(f);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    maxSize: 100 * 1024 * 1024,
  });

  const startExtraction = async () => {
    if (!file) return;
    setPhase('extracting');
    setProgressStep(0);
    setJobError(null);
    try {
      const created = await matApi.uploadAndExtract(file, presetProjectId);
      setJob(created);
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || 'Erreur lors de l\'upload du PDF';
      setJobError(msg);
      toast.error(msg);
      setPhase('upload');
    }
  };

  // ---------- POLLING (extracting) ----------
  useEffect(() => {
    if (phase !== 'extracting' || !job?.job_id) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const fresh = await matApi.getJob(job.job_id);
        if (cancelled) return;
        setJob(fresh);
        // Map status -> progressStep index for skeleton
        const stepIdx = { queued: 0, parsing: 0, extracting: 1, thumbs: 2, done: 3, error: 3 }[fresh.status] ?? 0;
        setProgressStep(stepIdx);
        if (fresh.status === 'done') {
          const result = await matApi.getJobResult(job.job_id);
          if (!cancelled) {
            setData(result);
            setPhase('validate');
            if (fresh.error) toast.warning(fresh.error, { duration: 8000 });
            else if (fresh.last_enrichment && fresh.cctp_file_name === file?.name) {
              const le = fresh.last_enrichment;
              toast.success(`CCTP fusionné : ${le.enriched} matériau(x) enrichi(s), ${le.added} ajouté(s)`, { duration: 7000 });
            }
          }
        } else if (fresh.status === 'error') {
          setJobError(fresh.error || 'Erreur extraction');
          toast.error(fresh.error || 'Erreur lors de l\'extraction IA');
          setPhase('upload');
        }
      } catch (e) {
        if (cancelled) return;
        console.error('poll error', e);
      }
    };
    poll();
    intervalRef.current = setInterval(poll, 2500);
    return () => { cancelled = true; clearInterval(intervalRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, job?.job_id]);

  // ---------- LOAD project info + latest job if ?project=xxx (and no ?job=xxx) ----------
  useEffect(() => {
    if (!presetProjectId) return;
    let cancelled = false;
    const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
    setLoadingProject(true);
    (async () => {
      try {
        // 1) Fetch project name for breadcrumb
        try {
          const prjRes = await axios.get(`${API_BASE}/projects/${presetProjectId}`, { withCredentials: true });
          if (!cancelled) setProjectMeta({ project_id: presetProjectId, name: prjRes.data?.name || 'Projet' });
        } catch {
          if (!cancelled) setProjectMeta({ project_id: presetProjectId, name: 'Projet' });
        }

        // 2) If a specific job was requested in URL, that takes precedence (handled by the other effect)
        if (presetJobId) return;

        // 3) Otherwise load the latest job for this project
        const latest = await matApi.getProjectLatestJob(presetProjectId);
        if (cancelled) return;
        const j = latest?.job;
        if (!j) {
          // No job yet -> upload state (default)
          return;
        }
        setJob(j);
        if (j.status === 'done' || j.approved_at) {
          try {
            const result = await matApi.getJobResult(j.job_id);
            if (cancelled) return;
            setData(result);
            setPhase('validate');
          } catch (e) {
            console.error('load result failed', e);
          }
        } else if (j.status === 'error') {
          setJobError(j.error || 'Job en erreur');
          setPhase('upload');
        } else {
          // running -> jump to extracting phase, polling effect will take over
          setPhase('extracting');
        }
      } finally {
        if (!cancelled) setLoadingProject(false);
      }
    })();
    return () => { cancelled = true; };
  }, [presetProjectId, presetJobId]);

  // ---------- LOAD existing job from URL (?job=mtj_...) ----------
  useEffect(() => {
    if (!presetJobId) return;
    let cancelled = false;
    (async () => {
      try {
        const fresh = await matApi.getJob(presetJobId);
        if (cancelled) return;
        setJob(fresh);
        if (fresh.status === 'done' || fresh.status === 'approved') {
          const result = await matApi.getJobResult(presetJobId);
          if (cancelled) return;
          setData(result);
          setPhase('validate');
        } else if (fresh.status === 'error') {
          setJobError(fresh.error || 'Job en erreur');
          setPhase('upload');
        } else {
          // still processing — switch to extracting phase to resume polling
          setPhase('extracting');
        }
      } catch (e) {
        console.error('load preset job failed', e);
        toast.error('Impossible de charger ce job de matériauthèque');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetJobId]);

  // ---------- VALIDATE actions ----------
  const editingMaterial = useMemo(() => {
    if (!editingId || !data) return null;
    for (const sec of data.sections) {
      const m = sec.items.find((i) => i.id === editingId);
      if (m) return m;
    }
    return null;
  }, [editingId, data]);

  const [generatingMaterialId, setGeneratingMaterialId] = useState(null);

  // ============================================================
  // Sprint Feb-2026 P2 — "Generate this material" focus mode.
  // Calls POST /api/projects/{id}/blueprint/render-3d with the
  // material's linked_file_ids as conditioning images.
  // ============================================================
  const handleGenerateMaterial = useCallback(async (materialId, materialName) => {
    if (!presetProjectId) {
      toast.error("Ce matériau n'est pas lié à un projet — génération impossible.");
      return;
    }
    setGeneratingMaterialId(materialId);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${presetProjectId}/blueprint/render-3d`,
        { focus_material_id: materialId },
        { withCredentials: true, timeout: 120000 },
      );
      const refsCount = r.data?.state?.client_refs_count ?? 0;
      toast.success(
        `Rendu généré pour « ${materialName} » (${refsCount} référence${refsCount > 1 ? 's' : ''} injectée${refsCount > 1 ? 's' : ''}) ✓`,
        { duration: 6000 },
      );
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Erreur de génération';
      toast.error(detail, { duration: 8000 });
    } finally {
      setGeneratingMaterialId(null);
    }
  }, [presetProjectId]);

  const updateMaterial = async (updated) => {
    try {
      const patch = {
        name: updated.name, ref: updated.ref, category: updated.category,
        finish: updated.finish, dimensions: updated.dimensions,
        thumb_color: updated.thumb_color, thumb_pattern: updated.thumb_pattern,
        // Sprint Feb-2026 C.a — linked starred image references
        linked_file_ids: updated.linked_file_ids || [],
      };
      await matApi.updateMaterial(updated.id, patch);
      setData((d) => recomputeStats({ ...d, sections: d.sections.map((s) => ({
        ...s,
        items: s.items.map((it) => it.id === updated.id ? { ...updated } : it),
      })) }));
      toast.success('Matériau mis à jour');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur sauvegarde');
    }
    setEditingId(null);
  };

  const deleteMaterial = async (id) => {
    if (!window.confirm('Supprimer ce matériau de l\'extraction ?')) return;
    try {
      await matApi.deleteMaterial(id);
      setData((d) => recomputeStats({
        ...d,
        sections: d.sections.map((s) => ({ ...s, items: s.items.filter((i) => i.id !== id) })),
      }));
      toast.success('Matériau supprimé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    }
    setEditingId(null);
  };

  const resetToAI = async (id) => {
    try {
      const restored = await matApi.resetMaterialToAi(id);
      // Map backend doc -> frontend item shape
      const mapped = {
        id: restored.material_id,
        name: restored.name,
        ref: restored.ref,
        category: restored.category,
        finish: restored.finish,
        dimensions: restored.dimensions,
        confidence: restored.confidence,
        source_page: restored.source_page,
        thumb_color: restored.thumb_color,
        thumb_pattern: restored.thumb_pattern,
        thumb: restored.thumb_url,
      };
      setData((d) => ({ ...d, sections: d.sections.map((s) => ({
        ...s,
        items: s.items.map((it) => it.id === id ? mapped : it),
      })) }));
      toast.message('Valeur IA restaurée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur restauration');
    }
  };

  const toggleSection = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  const handleApprove = async () => {
    if (!job?.job_id) return;
    setApproving(true);
    try {
      await matApi.approveJob(job.job_id, null);
      toast.success(`${data.stats.total} matériaux approuvés et appliqués au projet`, {
        description: 'Le moteur de rendu 3D a reçu la matériauthèque validée',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'approbation');
    }
    setApproving(false);
  };

  const handleReset = () => {
    if (!window.confirm('Recommencer une nouvelle extraction ? Les modifications non approuvées seront perdues.')) return;
    setPhase('upload');
    setSource(null);
    setFile(null);
    setProgressStep(0);
    setData(null);
    setJob(null);
    setJobError(null);
    setCollapsed({});
  };

  // ---------- HEADER ACTIONS (varient selon phase) ----------
  const historyDropdown = presetProjectId ? (
    <MaterialthequeHistoryDropdown projectId={presetProjectId} currentJobId={job?.job_id} />
  ) : null;

  const headerActions = phase === 'validate' ? (
    <>
      {historyDropdown}
      {projectMeta && (
        <HudButton
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/projects/${projectMeta.project_id}`)}
          leftIcon={<ArrowLeft className="w-3.5 h-3.5" />}
          data-testid="materialtheque-back-to-project"
        >
          Retour au projet
        </HudButton>
      )}
      <HudButton variant="ghost" size="sm" onClick={handleReset} testid="materialtheque-reset" leftIcon={<RefreshCw className="w-3.5 h-3.5" />} data-testid="materialtheque-reset">
        Nouvelle extraction
      </HudButton>
      {!isApproved && (
        <HudButton variant="secondary" size="sm" onClick={() => setAddOpen(true)} leftIcon={<Plus className="w-3.5 h-3.5" />} data-testid="materialtheque-add-material">
          Ajouter
        </HudButton>
      )}
      {!isApproved && (
        <HudButton variant="secondary" size="sm" onClick={handleDetectDuplicates} disabled={dupDetecting} leftIcon={<Copy className={`w-3.5 h-3.5 ${dupDetecting ? 'animate-pulse' : ''}`} />} data-testid="materialtheque-detect-duplicates">
          {dupDetecting ? 'Analyse…' : 'Doublons'}
          {data?.duplicate_groups?.length > 0 && !dupDetecting && (
            <span className="hud-tabular text-[10px] ml-1 px-1 py-0.5 rounded" style={{ background: 'rgba(217,160,91,0.2)', color: '#FDE68A' }}>{data.duplicate_groups.length}</span>
          )}
        </HudButton>
      )}
      {!isApproved && (
        <HudButton variant="secondary" size="sm" onClick={() => setEnrichOpen(true)} leftIcon={<GitMerge className="w-3.5 h-3.5" />} data-testid="materialtheque-enrich-cctp">
          Enrichir avec un CCTP
        </HudButton>
      )}
      {data && data.stats?.total > 0 && (
        <HudButton
          variant="secondary"
          size="sm"
          onClick={() => {
            const defaultName = projectMeta?.name
              ? `Style ${projectMeta.name}`
              : `Style ${job?.file_name?.replace(/\.[^.]+$/, '') || 'Matériauthèque'}`;
            setStyleName(defaultName);
            setStyleDescription('');
            setSaveStyleOpen(true);
          }}
          leftIcon={<Save className="w-3.5 h-3.5" />}
          data-testid="materialtheque-save-as-style"
        >
          Sauvegarder comme style
        </HudButton>
      )}
      {briefProjectId && (
        <ImportStyleMenu projectId={briefProjectId} onImported={async (r) => {
          toast.success(`Style « ${r.style_name} » importé (${r.materials_count} matériaux)`);
          setBriefKey((k) => k + 1);
          navigate(`/materialtheque?project=${briefProjectId}&job=${r.job_id}`);
        }} />
      )}
      {isApproved ? (
        <HudBadge tone="success" icon={CheckCircle2} data-testid="materialtheque-status-approved">
          Validée
        </HudBadge>
      ) : (
        <HudButton
          variant="primary"
          size="md"
          onClick={handleApprove}
          disabled={approving}
          leftIcon={<ShieldCheck className="w-4 h-4" />}
          data-testid="materialtheque-approve"
        >
          {approving ? 'Approbation…' : 'Approuver & Appliquer au Projet'}
        </HudButton>
      )}
    </>
  ) : phase === 'extracting' && (file || job) ? (
    <>
      {historyDropdown}
      <span className="hud-eyebrow flex items-center gap-2" style={{ color: 'var(--c-text-2)' }}>
        {job?.source === 'photos' ? <Images className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />} {file?.name || job?.file_name}
      </span>
    </>
  ) : projectMeta ? (
    <>
      {historyDropdown}
      <HudButton
        variant="ghost"
        size="sm"
        onClick={() => navigate(`/projects/${projectMeta.project_id}`)}
        leftIcon={<ArrowLeft className="w-3.5 h-3.5" />}
        data-testid="materialtheque-back-to-project"
      >
        Retour au projet
      </HudButton>
    </>
  ) : null;

  const breadcrumb = projectMeta
    ? [
        { label: 'Hub', to: '/studio/dashboard' },
        { label: projectMeta.name, to: `/projects/${projectMeta.project_id}` },
        { label: 'Matériauthèque IA' },
      ]
    : [
        { label: 'Hub', to: '/studio/dashboard' },
        { label: 'Matériauthèque IA' },
      ];

  return (
    <div data-hud-page data-testid="materialtheque-page" className="fade-in">
      <HUDPageHeader
        module="Cockpit"
        breadcrumb={breadcrumb}
        icon={Boxes}
        title="Matériauthèque IA"
        subtitle={
          isApproved
            ? 'Matériauthèque validée et appliquée au projet — prioritaire sur le style de décoration des lots ; les épinglages « Par pièce » restent modifiables et s\'appliquent au prochain rendu'
            : "Constituez la matériauthèque depuis vos photos 3D, un CCTP ou à la main — validation avant rendu 3D"
        }
        actions={headerActions}
      />

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {phase === 'upload' && !source && (
          <SourceChooser hasProject={!!presetProjectId} onPick={handlePickSource} busy={loadingProject} />
        )}
        {phase === 'upload' && source === 'cctp' && (
          <UploadState file={file} setFile={setFile} getRootProps={getRootProps} getInputProps={getInputProps} isDragActive={isDragActive} startExtraction={startExtraction} onBack={() => { setSource(null); setFile(null); }} />
        )}
        {phase === 'upload' && source === 'photos' && presetProjectId && (
          <PhotoSourcePanel projectId={presetProjectId} onBack={() => setSource(null)} onJobCreated={handlePhotosJobCreated} />
        )}
        {phase === 'extracting' && <ExtractingState progressStep={progressStep} fileName={file?.name || job?.file_name} job={job} />}
        {phase === 'validate'   && data && (
          <>
            {compareMode && presetProjectId && job?.job_id && (
              <div className="mb-5">
                <CctpDiffPanel projectId={presetProjectId} newJobId={job.job_id} />
              </div>
            )}
            {(dupOpen || data.duplicate_groups?.length > 0) && dupOpen !== 'closed' && (
              <div className="mb-5">
                <DuplicatesPanel
                  groups={data.duplicate_groups}
                  itemsById={Object.fromEntries(data.sections.flatMap((s) => s.items.map((i) => [i.id, { ...i, section_label: s.label }])))}
                  onMerge={handleMergeGroup}
                  onRedetect={handleDetectDuplicates}
                  onClose={() => setDupOpen('closed')}
                  busyGroupId={dupBusyGroup}
                  detecting={dupDetecting}
                  readOnly={isApproved}
                />
              </div>
            )}
            <MaterialBriefBanner projectId={briefProjectId} refreshKey={briefKey}
                                 onChanged={() => job?.job_id && reloadResult(job.job_id)} />
            <ValidateState
              data={data}
              collapsed={collapsed}
              onToggleSection={toggleSection}
              onEdit={(id) => setEditingId(id)}
              onRetain={handleRetain}
              onGenerateMaterial={handleGenerateMaterial}
              generatingMaterialId={generatingMaterialId}
              onAdd={!isApproved ? () => setAddOpen(true) : null}
              onEnrich={!isApproved ? () => setEnrichOpen(true) : null}
              onRenderRoom={(section) => setRoomRenderSection(section)}
              renderingSectionId={renderingSectionId}
            />
          </>
        )}
      </div>

      {createPortal(
        <>
          {roomRenderSection && (
            <RoomRenderDialog
              section={roomRenderSection}
              hasSourcePhoto={roomRenderSection.items.some((i) => i.source_file_id)}
              onClose={() => setRoomRenderSection(null)}
              onConfirm={handleRoomRender}
              busy={false}
            />
          )}

          {addOpen && (
            <AddMaterialDialog
              sections={data?.sections || []}
              roomTypes={ROOM_TYPES}
              onClose={() => setAddOpen(false)}
              onConfirm={handleAddMaterial}
              busy={adding}
            />
          )}

          {enrichOpen && (
            <CctpEnrichDialog
              materialsCount={data?.stats?.total || 0}
              onClose={() => setEnrichOpen(false)}
              onConfirm={handleEnrich}
              busy={enriching}
            />
          )}

          {editingMaterial && (
            <MaterialEditDrawer
              material={editingMaterial}
              onClose={() => setEditingId(null)}
              onSave={updateMaterial}
              onDelete={deleteMaterial}
              onResetToAI={resetToAI}
              onGenerate={handleGenerateMaterial}
              isGenerating={generatingMaterialId === (editingMaterial.id || editingMaterial.material_id)}
            />
          )}

          {saveStyleOpen && (
            <SaveAsStyleDialog
              name={styleName}
              setName={setStyleName}
              description={styleDescription}
              setDescription={setStyleDescription}
              onClose={() => setSaveStyleOpen(false)}
              onConfirm={handleSaveAsStyle}
              busy={savingStyle}
              materialsCount={data?.stats?.total || 0}
            />
          )}
        </>,
        document.body,
      )}
    </div>
  );
};

// ============================================================================
// State 1 — Upload
// ============================================================================
const UploadState = ({ file, setFile, getRootProps, getInputProps, isDragActive, startExtraction, onBack }) => {
  return (
    <div className="max-w-3xl mx-auto hud-stagger">
      {onBack && (
        <button type="button" onClick={onBack} className="hud-btn hud-btn-ghost hud-btn-sm mb-4" data-testid="materialtheque-cctp-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Autre source
        </button>
      )}
      <HudCard variant="glass" bracket className="text-center" style={{ padding: 0 }}>
        <span className="hud-bracket-bl" /><span className="hud-bracket-br" />
        <div
          {...getRootProps()}
          data-testid="materialtheque-dropzone"
          className={`relative cursor-pointer transition-all duration-300 px-8 py-16 sm:py-20 rounded-[24px] flex flex-col items-center gap-5 ${
            isDragActive ? 'bg-[#D9A05B]/8' : ''
          }`}
          style={{
            border: `1.5px dashed ${isDragActive ? 'var(--c-gold)' : 'rgba(255,255,255,0.20)'}`,
            margin: 12,
          }}
        >
          <input {...getInputProps()} />
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center"
            style={{
              background: 'var(--c-gold-soft)',
              border: '1px solid var(--c-border-accent)',
              boxShadow: 'var(--shadow-glow)',
            }}
          >
            {file ? (
              <FileCheck2 className="w-9 h-9" style={{ color: 'var(--c-gold)' }} strokeWidth={1.5} />
            ) : (
              <Upload className="w-9 h-9" style={{ color: 'var(--c-gold)' }} strokeWidth={1.5} />
            )}
          </div>

          {file ? (
            <div className="space-y-1">
              <span className="hud-eyebrow hud-eyebrow-gold">CCTP prêt à analyser</span>
              <p className="text-[18px] font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)' }}>
                {file.name}
              </p>
              <p className="text-[13px] hud-tabular" style={{ color: 'var(--c-text-muted)' }}>
                {(file.size / (1024 * 1024)).toFixed(2)} Mo · PDF
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-w-md">
              <p
                className="font-semibold tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 22 }}
              >
                Glissez-déposez le Carnet de Détails / CCTP du projet
                <span style={{ color: 'var(--c-gold)' }}> (Format PDF)</span>
              </p>
              <p className="text-[13.5px] leading-relaxed" style={{ color: 'var(--c-text-2)' }}>
                L'IA analysera le document pour extraire les références des sols,
                murs, faïences et menuiseries.
              </p>
            </div>
          )}

          <div className="flex items-center gap-2 text-[11px] hud-eyebrow" style={{ color: 'var(--c-text-muted)' }}>
            <Sparkles className="w-3 h-3" style={{ color: 'var(--c-gold)' }} />
            Nano Banana Vision · OCR + extraction structurée
          </div>
        </div>

        <div
          className="px-6 py-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3"
          style={{ borderTop: '1px solid var(--c-border)' }}
        >
          {file ? (
            <button
              type="button"
              className="hud-btn hud-btn-ghost hud-btn-sm"
              onClick={() => setFile(null)}
              data-testid="materialtheque-clear-file"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Changer de fichier
            </button>
          ) : (
            <span className="hud-eyebrow" style={{ color: 'var(--c-text-faint)' }}>
              Format accepté : PDF · Max 100 Mo
            </span>
          )}

          <HudButton
            variant="primary"
            size="md"
            disabled={!file}
            onClick={startExtraction}
            leftIcon={<Sparkles className="w-4 h-4" />}
            data-testid="materialtheque-launch"
          >
            Lancer l'extraction IA
          </HudButton>
        </div>
      </HudCard>
    </div>
  );
};

// ============================================================================
// State 2 — Extracting (skeleton + progress + messages)
// ============================================================================
const ExtractingState = ({ progressStep, fileName, job }) => {
  const fallback = EXTRACTION_PROGRESS_STEPS[progressStep] || EXTRACTION_PROGRESS_STEPS[0];
  const isPhotos = job?.source === 'photos';
  const useBackendStep = !!job?.source && !!job?.current_step;
  const step = {
    label: useBackendStep ? job.current_step : fallback.label,
    progress: useBackendStep && typeof job.progress === 'number' ? job.progress : fallback.progress,
  };
  const eyebrow = isPhotos ? 'Analyse des photos 3D' : 'Analyse IA en cours';

  return (
    <div className="max-w-4xl mx-auto space-y-6 hud-stagger">
      {/* Status */}
      <HudCard variant="glass">
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 relative"
            style={{
              background: 'var(--c-gold-soft)',
              border: '1px solid var(--c-border-accent)',
            }}
          >
            <Sparkles className="w-5 h-5 animate-pulse" style={{ color: 'var(--c-gold)' }} />
            <span
              aria-hidden
              className="absolute inset-0 rounded-xl"
              style={{ boxShadow: '0 0 24px rgba(217,160,91,0.35)', animation: 'hud-pulse 2s ease-in-out infinite' }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <span className="hud-eyebrow hud-eyebrow-gold">{eyebrow}</span>
            <p
              className="mt-1 font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 20 }}
              data-testid="materialtheque-progress-message"
            >
              {step.label}
            </p>
            {fileName && (
              <p className="text-[12.5px] mt-1 truncate" style={{ color: 'var(--c-text-muted)' }}>
                {isPhotos ? <Images className="inline w-3 h-3 mr-1" /> : <FileText className="inline w-3 h-3 mr-1" />}
                {fileName}
              </p>
            )}
            <div
              className="mt-4 h-1.5 rounded-full overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.08)' }}
              role="progressbar"
              aria-valuenow={step.progress}
              aria-valuemin={0}
              aria-valuemax={100}
              data-testid="materialtheque-progress-bar"
            >
              <div
                className="h-full transition-all duration-700 ease-out"
                style={{
                  width: `${step.progress}%`,
                  background: 'linear-gradient(90deg, var(--c-gold) 0%, var(--c-gold-hover) 100%)',
                  boxShadow: '0 0 12px rgba(217,160,91,0.5)',
                }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] hud-tabular" style={{ color: 'var(--c-text-muted)' }}>
              <span>{step.progress}%</span>
              <span className="hud-eyebrow">Étape {progressStep + 1} / {EXTRACTION_PROGRESS_STEPS.length}</span>
            </div>
          </div>
        </div>
      </HudCard>

      {/* Skeleton sections */}
      <div className="grid gap-4">
        {[0, 1, 2].map((i) => (
          <HudCard key={i} variant="glass" className="hud-skeleton-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="hud-skeleton-bar" style={{ width: 28, height: 28, borderRadius: 8 }} />
              <div className="hud-skeleton-bar" style={{ width: 180, height: 14, borderRadius: 4 }} />
              <div className="ml-auto hud-skeleton-bar" style={{ width: 60, height: 18, borderRadius: 6 }} />
            </div>
            <div className="grid gap-3">
              {[0, 1].map((j) => (
                <div key={j} className="flex items-center gap-3">
                  <div className="hud-skeleton-bar" style={{ width: 56, height: 56, borderRadius: 12 }} />
                  <div className="flex-1 space-y-2">
                    <div className="hud-skeleton-bar" style={{ width: '55%', height: 12, borderRadius: 4 }} />
                    <div className="hud-skeleton-bar" style={{ width: '38%', height: 10, borderRadius: 4 }} />
                  </div>
                  <div className="hud-skeleton-bar" style={{ width: 70, height: 22, borderRadius: 6 }} />
                </div>
              ))}
            </div>
          </HudCard>
        ))}
      </div>

      <style>{`
        @keyframes hud-skeleton-pulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }
        @keyframes hud-pulse {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.05); }
        }
        .hud-skeleton-bar {
          background: linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 100%);
          background-size: 200% 100%;
          animation: hud-skeleton-shimmer 1.6s ease-in-out infinite, hud-skeleton-pulse 2.2s ease-in-out infinite;
        }
        @keyframes hud-skeleton-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// State 3 — Validate
// ============================================================================
const ValidateState = ({ data, collapsed, onToggleSection, onEdit, onRetain, onGenerateMaterial, generatingMaterialId, onAdd, onEnrich, onRenderRoom, renderingSectionId }) => {
  const kind = data.source?.kind || 'cctp';
  const sourceTile = kind === 'photos'
    ? { icon: Camera, label: 'Source photos 3D', value: `${data.source.photos_count || 0} photo${(data.source.photos_count || 0) > 1 ? 's' : ''}`, subtitle: data.source.cctp_file_name ? `+ CCTP ${data.source.cctp_file_name}` : 'Vignettes découpées par l\'IA' }
    : kind === 'manual'
      ? { icon: Pencil, label: 'Source manuelle', value: 'Saisie', subtitle: data.source.cctp_file_name ? `+ CCTP ${data.source.cctp_file_name}` : 'Ajout matériau par matériau' }
      : { icon: FileText, label: 'Source CCTP', value: `${data.source.pages}p`, subtitle: data.source.file };
  const isEmpty = data.sections.every((s) => s.items.length === 0);
  return (
    <div className="space-y-6">
      {/* Récap KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 hud-stagger">
        <KpiTile icon={Boxes}         label="Matériaux extraits" value={data.stats.total}            tone="neutral" />
        <KpiTile icon={ShieldCheck}   label="Fiables (>90%)"     value={data.stats.high_confidence}  tone="success" />
        <KpiTile icon={AlertTriangle} label="À vérifier"          value={data.stats.needs_review + data.stats.low_confidence} tone="warning" />
        <KpiTile icon={sourceTile.icon} label={sourceTile.label} value={sourceTile.value} tone="gold" subtitle={sourceTile.subtitle} />
      </div>

      {isEmpty && (
        <HudCard variant="glass" className="text-center" data-testid="materialtheque-empty-state">
          <div className="py-8 flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'var(--c-gold-soft)', border: '1px solid var(--c-border-accent)' }}>
              <Boxes className="w-6 h-6" style={{ color: 'var(--c-gold)' }} strokeWidth={1.5} />
            </div>
            <p className="font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}>Matériauthèque vide</p>
            <p className="text-[13px] max-w-md" style={{ color: 'var(--c-text-2)' }}>Ajoutez vos matériaux à la main, ou fusionnez un CCTP dès qu'il est disponible.</p>
            <div className="flex gap-2 mt-2">
              {onAdd && <HudButton variant="primary" size="sm" onClick={onAdd} leftIcon={<Plus className="w-3.5 h-3.5" />} data-testid="materialtheque-empty-add">Ajouter un matériau</HudButton>}
              {onEnrich && <HudButton variant="secondary" size="sm" onClick={onEnrich} leftIcon={<GitMerge className="w-3.5 h-3.5" />} data-testid="materialtheque-empty-enrich">Fusionner un CCTP</HudButton>}
            </div>
          </div>
        </HudCard>
      )}

      {/* Sections */}
      <div className="space-y-4 hud-stagger">
        {data.sections.map((section) => {
          const Icon = ICON_MAP[section.icon] || Boxes;
          const isCollapsed = !!collapsed[section.id];
          return (
            <HudCard
              key={section.id}
              variant="glass"
              className="overflow-hidden"
              style={{ padding: 0 }}
              data-testid={`materialtheque-section-${section.id}`}
            >
              {/* Section header (cliquable) */}
              <button
                type="button"
                onClick={() => onToggleSection(section.id)}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition-colors"
                style={{ borderBottom: isCollapsed ? 'none' : '1px solid var(--c-border)' }}
                data-testid={`materialtheque-section-toggle-${section.id}`}
                aria-expanded={!isCollapsed}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{
                      background: 'var(--c-gold-soft)',
                      border: '1px solid var(--c-border-accent)',
                    }}
                  >
                    <Icon className="w-4 h-4" style={{ color: 'var(--c-gold)' }} strokeWidth={1.75} />
                  </div>
                  <div>
                    <h3
                      className="font-semibold tracking-tight"
                      style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 16 }}
                    >
                      {section.label}
                    </h3>
                    <span className="hud-eyebrow">
                      {section.items.length} matériau{section.items.length > 1 ? 'x' : ''}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {onRenderRoom && section.items.length > 0 && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); if (renderingSectionId !== section.id) onRenderRoom(section); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onRenderRoom(section); } }}
                      className="hud-btn hud-btn-sm"
                      data-testid={`section-render-room-${section.id}`}
                      title="Générer un rendu 3D photoréaliste de cette pièce avec ses matériaux"
                      style={{
                        background: 'rgba(217,160,91,0.14)',
                        border: '1px solid rgba(217,160,91,0.5)',
                        color: '#FBBF24',
                        opacity: renderingSectionId === section.id ? 0.7 : 1,
                        cursor: renderingSectionId === section.id ? 'progress' : 'pointer',
                      }}
                    >
                      <Camera className={`w-3.5 h-3.5 ${renderingSectionId === section.id ? 'animate-pulse' : ''}`} />
                      {renderingSectionId === section.id ? 'Rendu en cours…' : 'Générer la pièce'}
                      {section.renders?.length > 0 && renderingSectionId !== section.id && (
                        <span className="hud-tabular text-[10px] ml-0.5 px-1 py-0.5 rounded" style={{ background: 'rgba(0,0,0,0.25)', color: '#FDE68A' }}>{section.renders.length}</span>
                      )}
                    </span>
                  )}
                  <ChevronDown
                    className="w-4 h-4 transition-transform duration-200"
                    style={{
                      color: 'var(--c-text-muted)',
                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    }}
                  />
                </div>
              </button>

              {!isCollapsed && <SectionRendersStrip sectionId={section.id} renders={section.renders} />}

              {/* Items */}
              {!isCollapsed && (
                <div className="divide-y" style={{ borderColor: 'var(--c-border)' }}>
                  {section.items.map((m) => (
                    <MaterialRow
                      key={m.id}
                      m={m}
                      onEdit={() => onEdit(m.id)}
                      onRetain={onRetain ? () => onRetain(m.id) : null}
                      onGenerate={onGenerateMaterial ? () => onGenerateMaterial(m.id, m.name) : null}
                      isGenerating={generatingMaterialId === m.id}
                    />
                  ))}
                  {section.items.length === 0 && (
                    <div className="px-5 py-8 text-center text-[13px]" style={{ color: 'var(--c-text-muted)' }}>
                      Aucun matériau dans cette catégorie.
                    </div>
                  )}
                </div>
              )}
            </HudCard>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// Sub-components
// ============================================================================
const KpiTile = ({ icon: Icon, label, value, subtitle, tone = 'neutral' }) => {
  const accentColor = tone === 'success' ? 'var(--c-success)' :
                      tone === 'warning' ? 'var(--c-warning)' :
                      tone === 'gold'    ? 'var(--c-gold)' :
                      'var(--c-text-muted)';
  return (
    <div className="hud-card hud-card-glass relative" style={{ padding: 16, minHeight: 96 }}>
      <div className="flex items-start justify-between mb-2">
        <Icon className="w-4 h-4" style={{ color: accentColor }} strokeWidth={1.75} />
        <span className="hud-eyebrow text-right">{label}</span>
      </div>
      <p
        className="hud-tabular font-semibold leading-none"
        style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 28 }}
      >
        {value}
      </p>
      {subtitle && (
        <p className="text-[10.5px] mt-1.5 truncate" style={{ color: 'var(--c-text-muted)' }}>
          {subtitle}
        </p>
      )}
    </div>
  );
};

const MaterialRow = ({ m, onEdit, onRetain, onGenerate, isGenerating }) => {
  const tone = getConfidenceTone(m.confidence);
  const linkedCount = (m.linked_file_ids || []).length;
  const canGenerate = !!onGenerate && linkedCount > 0;
  return (
    <div
      className={`group flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-white/[0.025] ${tone.glow ? 'hud-row-warning' : ''}`}
      data-testid={`material-row-${m.id}`}
      style={tone.glow ? { boxShadow: 'inset 3px 0 0 0 var(--c-warning)' } : {}}
    >
      <MaterialThumbnail
        color={m.thumb_color}
        pattern={m.thumb_pattern}
        src={resolveThumbUrl(m.thumb)}
        size={56}
        rounded={12}
        label={m.name}
      />

      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium truncate" style={{ color: 'var(--c-text)' }}>
          {m.name}
        </p>
        <div className="mt-0.5 flex items-center gap-2 flex-wrap">
          {m.category && (
            <span className="text-[10px] px-1.5 py-0.5 rounded hud-eyebrow" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--c-text-2)' }}>
              {m.category}
            </span>
          )}
          {m.is_retained && (
            <span className="text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                  style={{ background: 'rgba(217,160,91,0.16)', color: 'var(--c-gold)', border: '1px solid rgba(217,160,91,0.5)' }}
                  data-testid={`material-retained-badge-${m.id}`} title="Retenu pour son rôle : seul ce matériau est envoyé à l'IA">
              <Star className="w-2.5 h-2.5" style={{ fill: 'var(--c-gold)' }} /> Retenu
            </span>
          )}
          {m.is_retained && (m.rooms || []).length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--c-text-2)' }}
                  data-testid={`material-rooms-${m.id}`}>
              {m.rooms.join(', ')}
            </span>
          )}
          {m.subrole_hint === 'sols_zone' && m.zone_label && (
            <span className="text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ background: 'rgba(34,197,94,0.12)', color: '#86efac', border: '1px solid rgba(34,197,94,0.35)' }}
                  title={`Sol dédié à la zone ouverte « ${m.zone_label} »${(m.zone_neighbours || []).length ? ` — ouverte sur ${m.zone_neighbours.join(', ')}` : ''}`}
                  data-testid={`material-zone-${m.id}`}>
              Zone ouverte · {m.zone_label}{(m.zone_neighbours || []).length ? ` ↔ ${m.zone_neighbours.join(', ')}` : ''}
            </span>
          )}
          {m.source === 'photo_extraction' && m.source_filename && (
            <span className="text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ background: 'rgba(217,160,91,0.10)', color: 'var(--c-gold)' }} title={`Extrait de ${m.source_filename}`} data-testid={`material-source-photo-${m.id}`}>
              <Camera className="w-2.5 h-2.5" /> {m.source_filename}
            </span>
          )}
          {m.cctp_match && (
            <span className="text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ background: 'rgba(52,211,153,0.10)', color: 'var(--c-success)' }} title={`CCTP : ${m.cctp_match.name}${m.cctp_match.source_page ? ` (p.${m.cctp_match.source_page})` : ''}`} data-testid={`material-cctp-match-${m.id}`}>
              <GitMerge className="w-2.5 h-2.5" /> CCTP{m.cctp_match.source_page ? ` p.${m.cctp_match.source_page}` : ''}
            </span>
          )}
          {m.rooms?.length > 1 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--c-text-2)' }} title={m.rooms.join(' · ')} data-testid={`material-rooms-${m.id}`}>
              <Copy className="w-2.5 h-2.5" /> {m.rooms.length} pièces
            </span>
          )}
          {m.ref && (
            <span className="text-[11px] hud-tabular" style={{ color: 'var(--c-text-muted)' }}>
              {m.ref}
            </span>
          )}
          {m.dimensions && (
            <>
              <span className="text-[10px]" style={{ color: 'var(--c-text-faint)' }}>·</span>
              <span className="text-[11px]" style={{ color: 'var(--c-text-muted)' }}>
                {m.dimensions}
              </span>
            </>
          )}
          {m.finish && (
            <>
              <span className="text-[10px]" style={{ color: 'var(--c-text-faint)' }}>·</span>
              <span className="text-[11px]" style={{ color: 'var(--c-text-muted)' }}>
                {m.finish}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <HudBadge tone={tone.tone} className={tone.glow ? 'hud-badge-glow' : ''}>
          <Sparkles className="w-3 h-3" />
          {formatConfidence(m.confidence)} · {tone.label}
        </HudBadge>
        {onGenerate && (
          <button
            type="button"
            onClick={canGenerate && !isGenerating ? onGenerate : undefined}
            disabled={!canGenerate || isGenerating}
            title={
              !canGenerate
                ? "Liez d'abord ≥1 référence visuelle à ce matériau (éditer → Références liées)"
                : isGenerating
                  ? "Génération en cours…"
                  : `Générer un rendu 3D focalisé sur ce matériau (${linkedCount} référence${linkedCount > 1 ? 's' : ''} liée${linkedCount > 1 ? 's' : ''})`
            }
            data-testid={`material-generate-${m.id}`}
            className="hud-btn hud-btn-sm"
            style={{
              background: canGenerate ? 'rgba(217,160,91,0.16)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${canGenerate ? 'rgba(217,160,91,0.55)' : 'var(--c-border)'}`,
              color: canGenerate ? '#FBBF24' : 'rgba(255,255,255,0.30)',
              cursor: !canGenerate || isGenerating ? 'not-allowed' : 'pointer',
            }}
          >
            <Sparkles className={`w-3.5 h-3.5 ${isGenerating ? 'animate-spin' : ''}`} />
            {isGenerating ? 'Génération…' : 'Générer'}
            {canGenerate && (
              <span
                className="hud-tabular text-[10px] ml-0.5 px-1 py-0.5 rounded"
                style={{ background: 'rgba(0,0,0,0.25)', color: '#FDE68A' }}
              >
                {linkedCount}
              </span>
            )}
          </button>
        )}
        {onRetain && (
          <button
            type="button"
            onClick={onRetain}
            className="hud-btn hud-btn-sm"
            title={m.is_retained ? 'Lever le choix (tous les candidats du rôle seront envoyés)' : 'Retenir ce matériau pour son rôle (les autres deviennent des alternatives)'}
            style={{
              background: m.is_retained ? 'rgba(217,160,91,0.18)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${m.is_retained ? 'rgba(217,160,91,0.6)' : 'var(--c-border)'}`,
              color: m.is_retained ? 'var(--c-gold)' : 'var(--c-text-2)',
            }}
            data-testid={`material-retain-${m.id}`}
          >
            <Star className="w-3.5 h-3.5" style={{ fill: m.is_retained ? 'var(--c-gold)' : 'none' }} />
            {m.is_retained ? 'Retenu' : 'Retenir'}
          </button>
        )}
        <button
          type="button"
          onClick={onEdit}
          className="hud-btn hud-btn-secondary hud-btn-sm"
          data-testid={`material-edit-${m.id}`}
        >
          <Pencil className="w-3.5 h-3.5" />
          Éditer
        </button>
      </div>

      <style>{`
        .hud-badge-glow {
          box-shadow: 0 0 14px rgba(234,179,8,0.25);
          animation: hud-badge-pulse 2.6s ease-in-out infinite;
        }
        @keyframes hud-badge-pulse {
          0%, 100% { box-shadow: 0 0 8px  rgba(234,179,8,0.15); }
          50%      { box-shadow: 0 0 18px rgba(234,179,8,0.40); }
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// V15.11 — Importer la Matériauthèque d'un autre projet (style sauvegardé)
// ============================================================================
const ImportStyleMenu = ({ projectId, onImported }) => {
  const [styles, setStyles] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/user/styles`, { withCredentials: true })
      .then((r) => setStyles((r.data || []).filter((s) => !s.is_predefined && (s.materials_count || 0) > 0)))
      .catch(() => setStyles([]));
  }, []);
  if (styles.length === 0) return null;
  const doImport = async (styleId) => {
    if (!window.confirm('Importer ce style comme Matériauthèque courante du projet ? Les matériaux actuels restent dans l\'historique.')) return;
    setBusy(true);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/materialtheque/projects/${projectId}/import-style`,
        { style_id: styleId }, { withCredentials: true });
      setOpen(false);
      onImported?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import impossible');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="relative">
      <HudButton variant="secondary" size="sm" onClick={() => setOpen((v) => !v)} disabled={busy}
                 leftIcon={<Upload className="w-3.5 h-3.5" />} data-testid="materialtheque-import-style">
        Importer d&apos;un autre projet
      </HudButton>
      {open && (
        <div className="absolute right-0 top-full mt-1 min-w-[280px] rounded-lg py-1 z-[100]"
             style={{ background: '#0d0d10', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="materialtheque-import-style-menu">
          {styles.map((s) => (
            <button key={s.style_id} type="button" onClick={() => doImport(s.style_id)} disabled={busy}
                    className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-white/[0.06]"
                    style={{ color: 'var(--c-text)' }} data-testid={`materialtheque-import-style-${s.style_id}`}>
              {s.name} <span style={{ color: 'var(--c-text-muted)' }}>· {s.materials_count} matériaux</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Save as Style Dialog
// ============================================================================
const SaveAsStyleDialog = ({ name, setName, description, setDescription, onClose, onConfirm, busy, materialsCount }) => {
  return (
    <>
      <div
        className="fixed inset-0 bg-black/65 backdrop-blur-sm z-[1400]"
        onClick={busy ? undefined : onClose}
        data-testid="materialtheque-save-style-backdrop"
      />
      <div
        role="dialog"
        aria-modal="true"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92%] max-w-[480px] rounded-2xl z-[1410]"
        style={{
          background: 'linear-gradient(to bottom, #0F0F12, #09090B)',
          border: '1px solid var(--c-border)',
          boxShadow: 'var(--shadow-cockpit)',
        }}
        data-testid="materialtheque-save-style-dialog"
      >
        <div className="px-6 py-5 flex items-start justify-between" style={{ borderBottom: '1px solid var(--c-border)' }}>
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Sauvegarde de style</span>
            <h3 className="mt-1 font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 20 }}>
              Réutiliser ces matériaux
            </h3>
            <p className="mt-1 text-[12.5px]" style={{ color: 'var(--c-text-muted)' }}>
              {materialsCount} matériaux seront embarqués dans le style
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="hud-btn hud-btn-ghost hud-btn-sm aspect-square px-0"
            data-testid="materialtheque-save-style-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="hud-eyebrow block mb-1.5" style={{ color: 'var(--c-text-muted)' }}>
              Nom du style *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              maxLength={120}
              placeholder="Ex: Style AMAIA GH15"
              className="w-full px-3 py-2 rounded-lg text-[14px] outline-none"
              style={{
                background: 'var(--c-surface-1)',
                border: '1px solid var(--c-border)',
                color: 'var(--c-text)',
              }}
              data-testid="materialtheque-save-style-name"
            />
          </div>
          <div>
            <label className="hud-eyebrow block mb-1.5" style={{ color: 'var(--c-text-muted)' }}>
              Description (optionnelle)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Ambiance, contexte, palette…"
              className="w-full px-3 py-2 rounded-lg text-[13.5px] outline-none resize-none"
              style={{
                background: 'var(--c-surface-1)',
                border: '1px solid var(--c-border)',
                color: 'var(--c-text)',
              }}
              data-testid="materialtheque-save-style-description"
            />
          </div>
        </div>

        <div className="px-6 py-4 flex items-center justify-end gap-2" style={{ borderTop: '1px solid var(--c-border)' }}>
          <HudButton variant="ghost" size="sm" onClick={onClose} disabled={busy} data-testid="materialtheque-save-style-cancel">
            Annuler
          </HudButton>
          <HudButton
            variant="primary"
            size="md"
            onClick={onConfirm}
            disabled={busy || !name.trim()}
            leftIcon={<Save className="w-4 h-4" />}
            data-testid="materialtheque-save-style-confirm"
          >
            {busy ? 'Sauvegarde…' : 'Sauvegarder le style'}
          </HudButton>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// Helpers
// ============================================================================
function recomputeStats(data) {
  const all = data.sections.flatMap((s) => s.items);
  return {
    ...data,
    stats: {
      total: all.length,
      high_confidence: all.filter((i) => i.confidence >= 0.90).length,
      needs_review:    all.filter((i) => i.confidence >= 0.80 && i.confidence < 0.90).length,
      low_confidence:  all.filter((i) => i.confidence < 0.80).length,
    },
  };
}

export default MaterialThequePage;
